import type { Filter, LogicalAndFilter } from '@cubejs-client/core'
import {
  CubeOperator,
  KeyValueFilterDict,
} from 'cube/components/CubeReportFilterRow'
import {
  CONVERSATION_FACTS_DIMENSIONS,
  CONVERSATION_FACTS_MEASURES,
  DRIFT_CHAT_APP_USERS_DIMENSIONS,
  DRIFT_CHAT_CLASSIFICATIONS_DIMENSIONS,
  DRIFT_CHAT_END_USERS_DIMENSIONS,
  DRIFT_CHAT_INBOX_DIMENSIONS,
  DRIFT_CHAT_OPPORTUNITY_OVERVIEW,
  DRIFT_CHAT_PLAYBOOK_DIMENSIONS,
  DRIFT_CHAT_TAGS_DIMENSIONS,
  DRIFT_CHAT_TEAMS_DIMENSIONS,
  DRIFT_CHAT_TOPIC_PREDICTIONS,
  DRIFT_CHAT_TOPICS,
  DRIFT_CHAT_TOPICS_PERFORMANCE_EXCLUDED_TOPICS,
} from 'cube/constants'
import { pathOr } from 'ramda'

import { type CubeQuery, CubeQueryBuilder } from '@driftt/dds.reports'

import { isNilOrEmpty } from 'utils/ramda'

export const isBinaryOperator = (
  operator: CubeOperator | null
): operator is CubeOperator => {
  if (!operator) {
    return false
  }
  return [
    CubeOperator.EQUALS,
    CubeOperator.NOT_EQUALS,
    CubeOperator.CONTAINS,
    CubeOperator.GREATER_THAN,
    CubeOperator.LESS_THAN_OR_EQUAL,
  ].includes(operator)
}

export const isUnaryOperator = (
  operator: CubeOperator | null
): operator is CubeOperator => {
  if (!operator) {
    return false
  }
  return [CubeOperator.SET, CubeOperator.NOT_SET].includes(operator)
}

export const operatorHasTextInput = (
  operator: CubeOperator | null
): operator is CubeOperator => {
  if (!operator) {
    return false
  }
  return [CubeOperator.CONTAINS, CubeOperator.DOES_NOT_CONTAIN].includes(
    operator
  )
}

export const operatorHasNumericInput = (
  operator: CubeOperator | null
): operator is CubeOperator => {
  if (!operator) {
    return false
  }
  return [CubeOperator.GREATER_THAN, CubeOperator.LESS_THAN_OR_EQUAL].includes(
    operator
  )
}

/** Receives a filter and validates that is a valid cube filter. This function can only validate single Filters, make sure be passing a Filter that is not LogicalAndFilter type. */
export const validateCubeFilter = (filter: Filter) => {
  const member = pathOr(null, ['member'], filter)
  const operator = pathOr(null, ['operator'], filter)
  const values = pathOr(null, ['values'], filter)

  if (isUnaryOperator(operator)) {
    return member && operator
  }
  return member && operator && !isNilOrEmpty(values)
}

/**
 * It validates that the cube filter is indeed a valid cube filter it takes in consideration that the Filter could be either BinaryFilter, UnaryFilter or LogicalAndFilter. In case of a LogicalAndFilter it validates each one of the filters in the "and" filters array.
 * */
export const isValidCubeFilter = (genericFilter: Filter) => {
  const { isKeyValue, keyValueFilters } =
    isCubeFilterKeyValueType(genericFilter)

  if (isKeyValue) {
    return keyValueFilters.every((filter) => validateCubeFilter(filter))
  }

  return validateCubeFilter(genericFilter)
}

// Fetches all playbooks used within the date range
export const formatPlaybookFilterOptionsQuery = (
  startDate: Date,
  endDate: Date
): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('PlaybookFilterOptions')
    .withQueryName('PlaybookFilterOptions')
    .withDimensions([
      DRIFT_CHAT_PLAYBOOK_DIMENSIONS.PLAYBOOK_NAME,
      CONVERSATION_FACTS_DIMENSIONS.PLAYBOOK_ID,
    ])
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      startDate,
      endDate
    )
    .withOrder([[DRIFT_CHAT_PLAYBOOK_DIMENSIONS.PLAYBOOK_NAME, 'asc']])
    .build()
}

// Fetches all topics ever used
export const formatTopicFilterOptionsQuery = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('TopicFilterOptions')
    .withQueryName('TopicFilterOptions')
    .withDimensions([DRIFT_CHAT_TOPICS.TOPIC_NAME, DRIFT_CHAT_TOPICS.TOPIC_ID])
    .withOrder([[DRIFT_CHAT_TOPICS.TOPIC_NAME, 'asc']])
    .build()
}

export const formatTopicsPerformanceExcludedTopicsQuery = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('TopicsPerformanceExcludedTopics')
    .withQueryName('TopicsPerformanceExcludedTopics')
    .withDimensions([
      DRIFT_CHAT_TOPICS_PERFORMANCE_EXCLUDED_TOPICS.TOPIC_NAME,
      DRIFT_CHAT_TOPICS_PERFORMANCE_EXCLUDED_TOPICS.TOPIC_ID,
    ])
    .withOrder([
      [DRIFT_CHAT_TOPICS_PERFORMANCE_EXCLUDED_TOPICS.TOPIC_NAME, 'asc'],
    ])
    .build()
}

export const formatBaseModelTopicsOptionsQuery = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('BaseModelTopicFilterOptions')
    .withQueryName('BaseModelTopicFilterOptions')
    .withDimensions([
      DRIFT_CHAT_TOPIC_PREDICTIONS.TOPIC_LABEL,
      DRIFT_CHAT_TOPIC_PREDICTIONS.TOPIC_ID,
    ])
    .withOrder([[DRIFT_CHAT_TOPIC_PREDICTIONS.TOPIC_LABEL, 'asc']])
    .build()
}

// Fetches all emails used within the date range
export const endUsersEmailFilterOptionsQuery = (
  startDate: Date,
  endDate: Date
): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('EndUsersEmailFilterOptions')
    .withQueryName('EndUsersEmailFilterOptions')
    .withDimensions([DRIFT_CHAT_END_USERS_DIMENSIONS.EMAIL])
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      startDate,
      endDate
    )
    .withOrder([[DRIFT_CHAT_END_USERS_DIMENSIONS.EMAIL, 'asc']])
    .build()
}

// Fetches all company names used within the date range
export const endUsersCompanyNameFilterOptionsQuery = (
  startDate: Date,
  endDate: Date
): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('EndUsersCompanyNameFilterOptions')
    .withQueryName('EndUsersCompanyNameFilterOptions')
    .withDimensions([DRIFT_CHAT_END_USERS_DIMENSIONS.COMPANY_NAME])
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      startDate,
      endDate
    )
    .withOrder([[DRIFT_CHAT_END_USERS_DIMENSIONS.COMPANY_NAME, 'asc']])
    .build()
}

// Fetch the list of all opportunity stages
export const opportunityStageOptions = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('OpportunityStageOptions')
    .withQueryName('OpportunityStageOptions')
    .withDimensions([DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_STAGE])
    .withOrder([[DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_STAGE, 'asc']])
    .build()
}

export const opportunityTypeOptions = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('OpportunityTypeOptions')
    .withQueryName('OpportunityTypeOptions')
    .withDimensions([DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_TYPE])
    .withOrder([[DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_TYPE, 'asc']])
    .build()
}

export const opportunitiesOptions = (
  startDate: Date,
  endDate: Date
): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('OpportunitiesOptions')
    .withQueryName('OpportunitiesOptions')
    .withDimensions([
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_ID,
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_NAME,
    ])
    .withinDateRange(
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
      startDate,
      endDate
    )
    .withFilters([
      {
        member: DRIFT_CHAT_OPPORTUNITY_OVERVIEW.CONVERSATION_COUNT,
        operator: CubeOperator.GREATER_THAN,
        values: ['0'],
      },
    ])
    .withOrder([[DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_NAME, 'asc']])
    .build()
}

export const formatTeamFilterOptionsQuery = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('TeamFilterOptions')
    .withQueryName('TeamFilterOptions')
    .withDimensions([
      DRIFT_CHAT_TEAMS_DIMENSIONS.TEAM_NAME,
      DRIFT_CHAT_TEAMS_DIMENSIONS.TEAM_ID,
    ])
    .withFilters([
      {
        member: DRIFT_CHAT_TEAMS_DIMENSIONS.TEAM_STATUS,
        operator: CubeOperator.EQUALS,
        values: ['ENABLED'],
      },
    ])
    .withOrder([[DRIFT_CHAT_TEAMS_DIMENSIONS.TEAM_NAME, 'asc']])
    .build()
}

export const formatLeadStageOptionsQuery = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('LeadStageFilterOptions')
    .withQueryName('LeadStageFilterOptions')
    .withDimensions([
      DRIFT_CHAT_CLASSIFICATIONS_DIMENSIONS.CLASSIFICATION_NAME,
      CONVERSATION_FACTS_DIMENSIONS.LAST_CLASSIFICATION_ID,
    ])
    .withOrder([
      [DRIFT_CHAT_CLASSIFICATIONS_DIMENSIONS.CLASSIFICATION_NAME, 'asc'],
    ])
    .build()
}

export const formatConversationStartedFromUrlQuery = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('ConversationStartedFromURLFilterOptions')
    .withQueryName('ConversationStartedFromURLFilterOptions')
    .withMeasures([CONVERSATION_FACTS_MEASURES.CONVERSATION_COUNT])
    .withDimensions([
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_FROM_URL,
    ])
    .withOrder([[CONVERSATION_FACTS_MEASURES.CONVERSATION_COUNT, 'desc']])
    .withFilters([
      {
        member: CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_FROM_URL,
        operator: 'set',
      },
    ])
    .withLimit(150)
    .build()
}

export const formatConversationTagOptionsQuery = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('ConversationTagFilterOptions')
    .withQueryName('ConversationTagFilterOptions')
    .withDimensions([DRIFT_CHAT_TAGS_DIMENSIONS.TAG_NAME])
    .withOrder([[DRIFT_CHAT_TAGS_DIMENSIONS.TAG_NAME, 'asc']])
    .withFilters([
      {
        member: DRIFT_CHAT_TAGS_DIMENSIONS.TAG_NAME,
        operator: 'set',
      },
    ])
    .build()
}

export const formatTeammatesOptionsQuery = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('TeammatesFilterOptions')
    .withQueryName('TeammatesFilterOptions')
    .withDimensions([
      DRIFT_CHAT_APP_USERS_DIMENSIONS.DISPLAY_NAME,
      DRIFT_CHAT_APP_USERS_DIMENSIONS.USER_ID,
    ])
    .withFilters([
      {
        member: DRIFT_CHAT_APP_USERS_DIMENSIONS.STATUS,
        operator: CubeOperator.EQUALS,
        values: ['ENABLED'],
      },
    ])
    .withOrder([[DRIFT_CHAT_APP_USERS_DIMENSIONS.DISPLAY_NAME, 'asc']])
    .build()
}

export const formatInboxOptionsQuery = (): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('InboxFilterOptions')
    .withQueryName('InboxFilterOptions')
    .withDimensions([
      DRIFT_CHAT_INBOX_DIMENSIONS.NAME,
      DRIFT_CHAT_INBOX_DIMENSIONS.ID,
    ])
    .withFilters([
      {
        member: DRIFT_CHAT_INBOX_DIMENSIONS.STATUS,
        operator: CubeOperator.EQUALS,
        values: ['ENABLED'],
      },
    ])
    .build()
}

export const gptEnabledPlaybooksOptionsQuery = (
  startDate: Date,
  endDate: Date
): CubeQuery => {
  return new CubeQueryBuilder({})
    .withName('GPTEnabledPlaybooksOptions')
    .withQueryName('GPTEnabledPlaybooksOptions')
    .withDimensions([
      DRIFT_CHAT_PLAYBOOK_DIMENSIONS.PLAYBOOK_NAME,
      DRIFT_CHAT_PLAYBOOK_DIMENSIONS.PLAYBOOK_ID,
    ])
    .withFilters([
      {
        member: DRIFT_CHAT_PLAYBOOK_DIMENSIONS.PLAYBOOK_IS_GPT_ENABLED,
        operator: CubeOperator.EQUALS,
        values: ['true'],
      },
    ])
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      startDate,
      endDate
    )
    .withOrder([[DRIFT_CHAT_PLAYBOOK_DIMENSIONS.PLAYBOOK_NAME, 'asc']])
    .build()
}

//#region LogicalAndFilter helpers

/**
 * LogicalAndFilter are a type of cube Filter that has only one property called "and" which is an array of Filters.
 * We are leveraging the LogicalAndFilter type for the filters that have a key and value filter.
 * @returns the keyValueFilters list of Filters and isKeyValue boolean.
 * */
export const isCubeFilterKeyValueType = (filter: Filter) => {
  if (!filter) {
    return {
      isKeyValue: false,
      keyValueFilters: [],
    }
  }

  const filtersAtAnd: Filter[] = pathOr([], ['and'], filter as LogicalAndFilter) // If the generic filter has a property called "and" it means it is a LogicalAndFilter type of filter.
  const isKeyValue = !!(filtersAtAnd.length > 0)

  return {
    keyValueFilters: filtersAtAnd,
    isKeyValue,
  }
}

/**
 * It receives a filter of type LogicalAndFilter and the cube member dimension string of the registered filter in the KeyValueFilterDict.
 * With those values it substracts the list of filters at "and" property and looks for the values on each filter (filter for key and filter for value)
 * */
export const getFilterValuesFromKeyValueFilters = (
  filter: LogicalAndFilter,
  cubeMemberDimension: string
) => {
  const { keyValueFilters } = isCubeFilterKeyValueType(filter)

  const { key, value } = KeyValueFilterDict[cubeMemberDimension]

  const keyFilter = keyValueFilters.find((f: Filter) => {
    return pathOr(null, ['member'], f) === key
  })

  const valueFilter = keyValueFilters.find((f: Filter) => {
    return pathOr(null, ['member'], f) === value
  })

  const selectedKeyValues = pathOr([], ['values'], keyFilter)
  const selectedValueValues = pathOr([], ['values'], valueFilter)

  return { key: selectedKeyValues, value: selectedValueValues }
}

//#endregion
